/**
 * §6.1 — THE 100% RULE, the weight tree, and the three helpers §6.1 names.
 *
 * *"Applies at every level: functional areas, capabilities within an area, sub-capabilities
 * within a capability. Weights are entered to one decimal place; the check is exactly 100.0.
 * Under 100: show `x% left to allocate`. Over 100: show `x% over`. Exactly 100: valid. A model
 * that does not balance can be saved as draft but cannot be baselined, and steps 4-9 stay
 * locked. Enforce on both client and server."*
 *
 * ── THIS FILE IS WHY THE PACKAGE EXISTS ────────────────────────────────────────────────
 *
 * D-009: *"New library has to be created for this app"*, and the reason it gives is this
 * rule — §6.1 requires the client to show a live running total and the server to refuse a
 * baseline, and *"a rule implemented twice is a rule that will eventually disagree with
 * itself."*
 *
 * The code below is MOVED, not rewritten. It is A4's
 * `apps/portal/server/src/erp_consolidation/weights/balance.ts`, which was written to be
 * moved — *"no Express, no database, no `req`, pure functions over plain arrays"* — and that
 * file is now a re-export of this one. Behaviour is unchanged on purpose: CLAUDE.md §1.2
 * rule 4 forbids reordering or cleaning up scoring math on my own judgement, and A4's
 * arithmetic already agrees with A8's specification.
 *
 * What this file ADDS to what A4 had:
 *
 *  - `checkBalance` — the FLAT sibling-set check (A8's `scoring-spec.ts` signature), which is
 *    what a client runs on one open group as the user types. `describeBalance` is the whole-
 *    tree version the server refuses a baseline on. Same arithmetic, two shapes, one source.
 *  - `distributeEvenly` and `normalizeProportionally` — the two helpers API_CONTRACT §3.2
 *    specifies, with the deterministic remainder rule. A4 deliberately left these out
 *    (*"§3.2 makes them pure client-side helpers"*), and §3.2 is explicit about why they
 *    belong HERE rather than behind an endpoint: *"A server route for them would be a second
 *    place the remainder rule lives, and the remainder rule is precisely the thing that must
 *    have exactly one implementation."*
 */
import { assertInteger, assertPctTenths, ONE_HUNDRED_PCT_TENTHS } from './units';

/* ──────────────────────────────────────────────────────────────────────────────────────
   THE FLAT CHECK — one sibling set
   ────────────────────────────────────────────────────────────────────────────────────── */

export interface BalanceResult {
  readonly totalTenths: number;
  readonly balanced: boolean;
  /** Signed: negative means "left to allocate", positive means "over". */
  readonly deltaTenths: number;
  /** §6.1's own wording: "x% left to allocate" / "x% over" / "valid". */
  readonly message: string;
}

/**
 * §6.1's check on ONE sibling set — the live running total a screen shows while somebody types.
 *
 * An EMPTY set reports `0` tenths and therefore "100.0% left to allocate", which is the honest
 * answer to "do these weights sum to 100?" when there are none. Emptiness as a REFUSAL is a
 * different error with a different message (`capability_model_is_empty`, API_CONTRACT §6.12)
 * and is not this function's job — see `describeBalance`'s note on why folding the two together
 * would make one error say two things.
 */
export function checkBalance(weightsTenths: readonly number[]): BalanceResult {
  for (const w of weightsTenths) assertPctTenths(w, 'checkBalance: weight');
  const totalTenths = weightsTenths.reduce((a, b) => a + b, 0);
  const deltaTenths = totalTenths - ONE_HUNDRED_PCT_TENTHS;
  return {
    totalTenths,
    balanced: deltaTenths === 0,
    deltaTenths,
    message: formatBalanceMessage(deltaTenths),
  };
}

/**
 * §6.1's two sentences, and the ONE place they are produced.
 *
 * *"Under 100: show `x% left to allocate`. Over 100: show `x% over`."* The server fills every
 * `message` by calling this, and the client calls the same function for its live total, so the
 * sentence on screen while somebody types is byte-identical to the sentence in the refusal
 * they get if they baseline anyway. Rebuilding the sentence in the browser is how those two
 * drift apart — and the one on the refusal is the one an auditor reads.
 *
 * `toFixed(1)` because §6.1 is explicit that weights are entered to ONE decimal place: a
 * deficit of 45 tenths is "4.5%", and a deficit of 30 is "3.0%" and not "3%".
 */
export function formatBalanceMessage(deltaTenths: number): string {
  const magnitude = (Math.abs(deltaTenths) / 10).toFixed(1);
  if (deltaTenths === 0) return 'valid';
  return deltaTenths < 0 ? `${magnitude}% left to allocate` : `${magnitude}% over`;
}

/** The `message` on the 409 envelope itself, above the per-group sentences. */
export function summariseBalance(groups: readonly WeightGroup[]): string {
  return groups.length === 1
    ? '1 weight group does not total 100.0%.'
    : `${groups.length} weight groups do not total 100.0%.`;
}

/* ──────────────────────────────────────────────────────────────────────────────────────
   THE TREE — every sibling set in one model
   ────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The shape both trees in this schema reduce to: `capability` and `capability_template_line`.
 *
 * Two tables, one rule. `0094` makes them the same tree on purpose — *"Arbitrary depth, same
 * tree shape as capability. A top-level line is a functional area."* — and a template that
 * balances under a different rule than the model it instantiates into is a template that
 * produces an unbalanceable model.
 */
export interface WeightNode {
  readonly id: string;
  /** `null` means this node is a FUNCTIONAL AREA. There is deliberately no level column. */
  readonly parentId: string | null;
  readonly name: string;
  readonly weightTenths: number;
  readonly sortOrder: number;
}

/** One sibling set that does not balance. API_CONTRACT §3.1 fixes every key and every value. */
export interface WeightGroup {
  /** `null` = the functional-area level. */
  readonly parentCapabilityId: string | null;
  readonly parentName: string | null;
  readonly level: 'functional_area' | 'capability';
  readonly childCount: number;
  readonly totalTenths: number;
  /** Signed: negative = under, positive = over. */
  readonly deltaTenths: number;
  readonly state: 'under' | 'over';
  /** §6.1 VERBATIM. The UI shows this unaltered. */
  readonly message: string;
}

export interface WeightBalance {
  readonly balanced: boolean;
  /** ONLY the groups that do not balance, in tree order. Empty when balanced. */
  readonly groups: readonly WeightGroup[];
}

/**
 * Every unbalanced sibling set in one tree.
 *
 * A GROUP is a parent and its children, and the functional areas form the group whose parent
 * is `null`. A node with no children forms no group — there is nothing to sum — which is why a
 * leaf capability at 40.0% is not itself a violation of anything.
 *
 * **Order is functional areas first, then depth-first**, which is what API_CONTRACT §3.1
 * promises and what lets a screen show the deficits in the order the tree is drawn.
 *
 * An EMPTY tree is reported BALANCED, with no groups. It has no sibling set that fails, so
 * there is nothing for this function to report — and emptiness is refused separately and by
 * name (`capability_model_is_empty`, API_CONTRACT §6.12). Folding the two together here would
 * make one error say two things, and the screen could not tell "add a functional area" from
 * "the weights are wrong".
 */
export function describeBalance(nodes: readonly WeightNode[]): WeightBalance {
  const children = childrenByParent(nodes);
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const groups: WeightGroup[] = [];

  const consider = (parentId: string | null): void => {
    const set = children.get(parentId);
    if (!set || set.length === 0) return;
    const totalTenths = set.reduce((sum, node) => sum + node.weightTenths, 0);
    const deltaTenths = totalTenths - ONE_HUNDRED_PCT_TENTHS;
    if (deltaTenths === 0) return;
    const parent = parentId === null ? null : (byId.get(parentId) ?? null);
    groups.push({
      parentCapabilityId: parentId,
      parentName: parent === null ? null : parent.name,
      level: parentId === null ? 'functional_area' : 'capability',
      childCount: set.length,
      totalTenths,
      deltaTenths,
      state: deltaTenths < 0 ? 'under' : 'over',
      message: formatBalanceMessage(deltaTenths),
    });
  };

  consider(null);
  for (const node of orderTree(nodes)) consider(node.id);

  return { balanced: groups.length === 0, groups };
}

/**
 * Depth-first, parents before children, siblings by `(sort_order, name, id)`.
 *
 * The third key is not decoration. `sort_order` has no uniqueness constraint and two siblings
 * routinely share one (everything created in a batch gets 0), and `name` is not unique either
 * — so without `id` the order of two capabilities would depend on the order Postgres happened
 * to return them, and a tree would reorder itself between two reads of the same unchanged
 * data.
 *
 * Nodes unreachable from a root — which a cycle would produce, and which the write paths
 * refuse before one can exist — are appended at the end rather than dropped. This function is
 * also what a repair script would read, and a node that has vanished from a listing is harder
 * to find than one in the wrong place.
 */
export function orderTree<T extends WeightNode>(nodes: readonly T[]): T[] {
  const children = childrenByParent(nodes);
  const ordered: T[] = [];
  const seen = new Set<string>();

  const walk = (parentId: string | null): void => {
    for (const node of (children.get(parentId) ?? []) as T[]) {
      if (seen.has(node.id)) continue;
      seen.add(node.id);
      ordered.push(node);
      walk(node.id);
    }
  };

  walk(null);
  for (const node of nodes) if (!seen.has(node.id)) ordered.push(node);
  return ordered;
}

/**
 * §6.1 — effective weight: *"the product of weights down the tree"*, in integer tenths,
 * **floored at each level**.
 *
 * A functional area's effective weight is its own weight. A capability at 40.0% inside an area
 * at 25.0% is 10.0% — `floor(250 * 400 / 1000) = 100`. The unit is tenths of a percentage
 * point OF THE WHOLE MODEL, which is the only unit in which the numbers across two different
 * areas can be compared at all.
 *
 * `0094` is explicit that this is NOT a column: *"Storing it would need every ancestor's
 * update to fan out, and the first missed fan-out is a wrong score that looks right."* So it
 * is computed on read, here, once — and the floor happens in exactly one place, which is the
 * reason this is a function and not an expression repeated per level.
 *
 * FLOOR, not `roundHalfUp`, and deliberately so: A4 chose it, API_CONTRACT §3.3 states it
 * (*"The product is taken in integer tenths and floored at each level"*), and CLAUDE.md §1.2
 * rule 4 forbids me changing it on my own judgement. It is also the conservative direction —
 * a rounded-up effective weight could make a set of siblings sum to more than their parent.
 */
export function effectiveWeights(nodes: readonly WeightNode[]): Map<string, number> {
  const effective = new Map<string, number>();
  for (const node of orderTree(nodes)) {
    if (node.parentId === null) {
      effective.set(node.id, node.weightTenths);
      continue;
    }
    const parent = effective.get(node.parentId);
    /*
     * A parent whose effective weight is not known yet cannot happen for a node reached
     * through `orderTree` from a root — parents always precede children there. It CAN happen
     * for one of that function's unreachable stragglers, and 0 is the honest answer for a node
     * hanging off nothing: it contributes nothing to any score, which is exactly what being
     * detached from the tree means.
     */
    effective.set(node.id, parent === undefined ? 0 : Math.floor((parent * node.weightTenths) / 1000));
  }
  return effective;
}

/** Every descendant of one node, and the node itself. Used by the delete guards. */
export function subtreeIds(nodes: readonly WeightNode[], rootId: string): string[] {
  const children = childrenByParent(nodes);
  const ids: string[] = [];
  const walk = (id: string): void => {
    ids.push(id);
    for (const child of children.get(id) ?? []) walk(child.id);
  };
  walk(rootId);
  return ids;
}

/**
 * Would making `parentId` the parent of `nodeId` close a cycle?
 *
 * The self-referencing foreign key accepts `A -> B -> A` happily — `0094` adds no constraint
 * against it and none is expressible — and a cycle makes the tree unrenderable and any
 * recursive read non-terminating. So every reparent asks this first.
 *
 * Walking UP from the proposed parent is the cheap direction: it terminates at a root in the
 * depth of the tree, where walking down would visit the whole subtree. `visited` guards against
 * a cycle that somehow already exists, so this function cannot be the thing that hangs while
 * checking for the thing that hangs.
 */
export function wouldCycle(
  nodes: readonly WeightNode[],
  nodeId: string,
  parentId: string | null,
): boolean {
  if (parentId === null) return false;
  if (parentId === nodeId) return true;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const visited = new Set<string>();
  let cursor: string | null = parentId;
  while (cursor !== null && !visited.has(cursor)) {
    if (cursor === nodeId) return true;
    visited.add(cursor);
    cursor = byId.get(cursor)?.parentId ?? null;
  }
  return false;
}

/** Depth from the root, 0 for a functional area. Convenience for the screens; never stored. */
export function depths(nodes: readonly WeightNode[]): Map<string, number> {
  const children = childrenByParent(nodes);
  const depth = new Map<string, number>();
  const walk = (parentId: string | null, level: number): void => {
    for (const node of children.get(parentId) ?? []) {
      if (depth.has(node.id)) continue;
      depth.set(node.id, level);
      walk(node.id, level + 1);
    }
  };
  walk(null, 0);
  for (const node of nodes) if (!depth.has(node.id)) depth.set(node.id, 0);
  return depth;
}

/** How many direct children each node has. */
export function childCounts(nodes: readonly WeightNode[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of nodes) counts.set(node.id, 0);
  for (const node of nodes) {
    // `counts.has` and not `?? 0`: a parent id naming nothing in this set must not invent an
    // entry for a row that does not exist.
    if (node.parentId === null || !counts.has(node.parentId)) continue;
    counts.set(node.parentId, (counts.get(node.parentId) ?? 0) + 1);
  }
  return counts;
}

function childrenByParent(nodes: readonly WeightNode[]): Map<string | null, WeightNode[]> {
  const known = new Set(nodes.map((node) => node.id));
  const children = new Map<string | null, WeightNode[]>();
  for (const node of nodes) {
    /*
     * A node whose `parentId` names something not in this set belongs to NO group. It happens
     * for one reason only — a caller handed in a subset of a tree — and hanging it off `null`
     * would silently add it to the functional-area group and report a deficit that is an
     * artefact of the slice. It is omitted here instead, and `orderTree` still returns it (as
     * one of its stragglers) so it cannot disappear from a listing. Every caller in this module
     * reads the WHOLE tree, which is why this case should never arise in the first place.
     */
    if (node.parentId !== null && !known.has(node.parentId)) continue;
    const bucket = children.get(node.parentId);
    if (bucket === undefined) children.set(node.parentId, [node]);
    else bucket.push(node);
  }
  for (const bucket of children.values()) bucket.sort(compareSiblings);
  return children;
}

function compareSiblings(a: WeightNode, b: WeightNode): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/* ──────────────────────────────────────────────────────────────────────────────────────
   §6.1's THREE HELPERS — *"distribute evenly, normalize proportionally, lock individual
   weights."*

   The third is not a computation: `capability.weight_locked` is persisted state (`0094`), set
   through `PATCH …/capabilities/:id`. What the two COMPUTATIONS owe it is to leave it alone —
   `0094`: *"The distribute-evenly and normalize-proportionally helpers must leave a locked
   weight alone"* — and to distribute only `1000 - Σ locked`.
   ────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Siblings whose weights the helpers operate on.
 *
 * SNAKE_CASE, and not by accident: API_CONTRACT §3.2 fixes these exact field names as the
 * package's contract (*"A3 owns the package; these are its contract"*), and they are also the
 * column names, so a row read straight out of `capability` is already a `WeightSibling`. The
 * rest of this package uses camelCase because the rest of this package is not pinned by a
 * published signature.
 */
export interface WeightSibling {
  readonly capability_id: string;
  readonly weight_tenths: number;
  readonly weight_locked: boolean;
  /** Ties broken by `capability_id`, as the DB orders. */
  readonly sort_order: number;
}

export interface WeightChange {
  readonly capability_id: string;
  readonly weight_tenths: number;
}

/**
 * A refusal from a helper, as opposed to a programming error.
 *
 * `code` is the snake_case vocabulary API_CONTRACT §1.6 uses on the wire, so a route can
 * forward it without inventing a second name for the same condition.
 */
export class WeightHelperError extends Error {
  readonly code: 'locked_weights_exceed_100';
  constructor(code: 'locked_weights_exceed_100', message: string) {
    super(message);
    this.name = 'WeightHelperError';
    this.code = code;
  }
}

export interface WeightCapacity {
  readonly lockedTenths: number;
  /** `1000 - Σ locked`. Negative means the locks alone are over 100.0%. */
  readonly availableTenths: number;
  readonly unlockedCount: number;
  /** `null` when both helpers would succeed. */
  readonly refusal: 'locked_weights_exceed_100' | null;
}

/**
 * What the helpers have to work with, WITHOUT throwing.
 *
 * A client needs to disable a button, not catch an exception, and `distributeEvenly` returns
 * `WeightChange[]` by contract so its only way to refuse is to throw. This is the same test,
 * available as a value. It is the one addition here that §3.2 does not name, and it exists
 * because the alternative is a `try`/`catch` in a render path.
 */
export function weightCapacity(siblings: readonly WeightSibling[]): WeightCapacity {
  let lockedTenths = 0;
  let unlockedCount = 0;
  for (const s of siblings) {
    assertPctTenths(s.weight_tenths, `weight_tenths for ${s.capability_id}`);
    if (s.weight_locked) lockedTenths += s.weight_tenths;
    else unlockedCount++;
  }
  const availableTenths = ONE_HUNDRED_PCT_TENTHS - lockedTenths;
  return {
    lockedTenths,
    availableTenths,
    unlockedCount,
    refusal: availableTenths < 0 ? 'locked_weights_exceed_100' : null,
  };
}

/**
 * §6.1 *"distribute evenly"*, with §6.1's deterministic remainder rule.
 *
 * *"When evenly distributing, assign the rounding remainder deterministically (e.g. to the
 * first sibling) so the total is exactly 100.0."* `0094` and API_CONTRACT §3.2 already fixed
 * the choice this spec left open: **the whole remainder goes to the FIRST sibling**, first
 * meaning first in `(sort_order ASC, capability_id ASC)` — the DB's own order, *"Not 'first in
 * the array the browser happened to build', which is how two clients get two answers."*
 *
 *     unlocked  = siblings where !weight_locked, in (sort_order, capability_id) order
 *     available = 1000 - Σ(locked weights)
 *     base      = floor(available / unlocked.length)
 *     remainder = available - base × unlocked.length          // 0 .. length-1
 *     every unlocked sibling     -> base
 *     the FIRST unlocked sibling -> base + remainder          // the whole remainder, one place
 *
 *     7 unlocked, nothing locked:  base 142, remainder 6
 *                                  -> 148, 142, 142, 142, 142, 142, 142  = 1000 exactly
 *
 * RETURNS ONE ENTRY PER SIBLING, in that same order — §3.2: *"Returns a full replacement set
 * summing to exactly 1000."* A locked sibling appears carrying the weight it already had, so
 * the set literally sums to 1000 and a locked weight is still untouched. Returning only the
 * changed rows would satisfy the second sentence and not the first.
 *
 * With NOTHING unlocked there is nowhere to put the weight, so the set comes back exactly as
 * it went in — §3.2's *"if unlocked is empty -> no change"*. Whether it balances is then
 * `checkBalance`'s answer, not this function's; silently unlocking something to make it
 * balance is the *"never redistribute silently"* failure in its most tempting form.
 */
export function distributeEvenly(siblings: readonly WeightSibling[]): WeightChange[] {
  const ordered = [...siblings].sort(compareSiblingRows);
  const capacity = weightCapacity(ordered);
  if (capacity.refusal !== null) {
    throw new WeightHelperError(
      capacity.refusal,
      `the locked weights alone total ${(capacity.lockedTenths / 10).toFixed(1)}%, which is over 100.0%`,
    );
  }

  const unlocked = ordered.filter((s) => !s.weight_locked);
  if (unlocked.length === 0) return ordered.map(unchanged);

  const base = Math.floor(capacity.availableTenths / unlocked.length);
  const remainder = capacity.availableTenths - base * unlocked.length;
  const firstUnlockedId = unlocked[0]!.capability_id;

  return ordered.map((s) => {
    if (s.weight_locked) return unchanged(s);
    return {
      capability_id: s.capability_id,
      weight_tenths: s.capability_id === firstUnlockedId ? base + remainder : base,
    };
  });
}

/**
 * §6.1 *"normalize proportionally"* — scale the unlocked weights so the set sums to 1000.
 *
 * API_CONTRACT §3.2: *"scales each unlocked weight by `available / Σ(unlocked)`, floors each,
 * then gives the ENTIRE residual to the first unlocked sibling in the same order — so the
 * total is exactly 1000 by the same mechanism. Where `Σ(unlocked)` is `0` it falls back to
 * `distributeEvenly`, because there is no proportion to preserve."*
 *
 * FLOOR each, then one residual — not `roundHalfUp` each. Rounding each share independently
 * does not sum to the target (seven shares of 1000 rounded half-up can total 1001), and the
 * fix would be a second adjustment pass whose direction depends on which way the rounding
 * went. Flooring puts the entire discrepancy in one known place, which is the same mechanism
 * §6.1 chose for distribute-evenly, and it is why both helpers have the same remainder rule
 * rather than two.
 *
 * The residual lands on the first unlocked sibling even when that makes it the largest, which
 * can nudge a proportion by up to `unlocked.length - 1` tenths — at most 0.1% per sibling.
 * That is the cost of the total being exactly 100.0%, which §6.1 requires absolutely and
 * proportionality only approximately.
 */
export function normalizeProportionally(siblings: readonly WeightSibling[]): WeightChange[] {
  const ordered = [...siblings].sort(compareSiblingRows);
  const capacity = weightCapacity(ordered);
  if (capacity.refusal !== null) {
    throw new WeightHelperError(
      capacity.refusal,
      `the locked weights alone total ${(capacity.lockedTenths / 10).toFixed(1)}%, which is over 100.0%`,
    );
  }

  const unlocked = ordered.filter((s) => !s.weight_locked);
  if (unlocked.length === 0) return ordered.map(unchanged);

  const unlockedTotal = unlocked.reduce((sum, s) => sum + s.weight_tenths, 0);
  if (unlockedTotal === 0) return distributeEvenly(ordered);

  const scaled = new Map<string, number>();
  let allocated = 0;
  for (const s of unlocked) {
    const share = Math.floor((s.weight_tenths * capacity.availableTenths) / unlockedTotal);
    scaled.set(s.capability_id, share);
    allocated += share;
  }
  const residual = capacity.availableTenths - allocated;
  const firstUnlockedId = unlocked[0]!.capability_id;
  scaled.set(firstUnlockedId, (scaled.get(firstUnlockedId) ?? 0) + residual);

  return ordered.map((s) =>
    s.weight_locked
      ? unchanged(s)
      : { capability_id: s.capability_id, weight_tenths: scaled.get(s.capability_id) ?? 0 },
  );
}

function unchanged(s: WeightSibling): WeightChange {
  return { capability_id: s.capability_id, weight_tenths: s.weight_tenths };
}

/** `(sort_order ASC, capability_id ASC)` — API_CONTRACT §3.2's definition of "first". */
function compareSiblingRows(a: WeightSibling, b: WeightSibling): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.capability_id < b.capability_id ? -1 : a.capability_id > b.capability_id ? 1 : 0;
}

/* ──────────────────────────────────────────────────────────────────────────────────────
   EFFECTIVE WEIGHT AS A ROW INPUT

   §6.2, §6.3 and §6.6 are all *"Σ (effective weight × something)"*, and the something is per
   capability. This is the one place that turns a tree into the flat `{ id, weightTenths }`
   list those three take, so no caller does the walk itself.
   ────────────────────────────────────────────────────────────────────────────────────── */

export interface EffectiveWeightRow {
  readonly id: string;
  readonly effectiveWeightTenths: number;
}

/**
 * The effective weight of every node, in tree order, as a flat list.
 *
 * NOT filtered to leaves. Which nodes a score sums over is the caller's question — §6.2 sums
 * over the capabilities one assessment unit holds a cell for, which is a set the grid decides
 * — and a package that quietly dropped the parents would make a two-level model score
 * differently from a one-level model carrying the same leaves.
 */
export function effectiveWeightRows(nodes: readonly WeightNode[]): EffectiveWeightRow[] {
  const effective = effectiveWeights(nodes);
  return orderTree(nodes).map((node) => ({
    id: node.id,
    effectiveWeightTenths: effective.get(node.id) ?? 0,
  }));
}

/**
 * Rescale a set of effective weights so they sum to exactly 1000 for ONE unit.
 *
 * §6.2's weights sum to 100% *within the unit being scored* — which is why §6.2's worked
 * example is seven weights totalling 100 and not a whole-model tree (an assessment unit is
 * BU × application × functional area, `0096`). A caller scoring one unit from whole-model
 * effective weights therefore holds weights that sum to that AREA's weight, not to 1000, and
 * this is the conversion.
 *
 * Same mechanism as `normalizeProportionally` — floor each, entire residual to the first — so
 * the module has one remainder rule and not two. Order is the order given, because these rows
 * already came out of `orderTree`.
 */
export function rescaleToFullWeight(
  rows: readonly EffectiveWeightRow[],
): EffectiveWeightRow[] {
  const total = rows.reduce((sum, r) => sum + assertInteger(r.effectiveWeightTenths, 'effectiveWeightTenths'), 0);
  if (rows.length === 0 || total === 0 || total === ONE_HUNDRED_PCT_TENTHS) return [...rows];
  const out = rows.map((r) => ({
    id: r.id,
    effectiveWeightTenths: Math.floor((r.effectiveWeightTenths * ONE_HUNDRED_PCT_TENTHS) / total),
  }));
  const allocated = out.reduce((sum, r) => sum + r.effectiveWeightTenths, 0);
  const first = out[0]!;
  out[0] = {
    id: first.id,
    effectiveWeightTenths: first.effectiveWeightTenths + (ONE_HUNDRED_PCT_TENTHS - allocated),
  };
  return out;
}

/**
 * §6.1's *"Changing a baselined model creates a new version and flags affected assessments."*
 *
 * The VERSIONING is a server action. What is arithmetic — and therefore here — is deciding
 * which capabilities actually moved between two weight sets, because that is the set of
 * assessments to flag. A capability whose weight is unchanged affects no score.
 *
 * Reported as three disjoint lists rather than one diff, because the three have different
 * consequences: a changed weight rescores, an added capability needs a cell in every unit, and
 * a removed one leaves its cells orphaned.
 */
export interface WeightDelta {
  readonly changed: readonly { readonly id: string; readonly fromTenths: number; readonly toTenths: number }[];
  readonly added: readonly string[];
  readonly removed: readonly string[];
  readonly anyChange: boolean;
}

export function diffWeights(
  before: readonly WeightNode[],
  after: readonly WeightNode[],
): WeightDelta {
  const beforeById = new Map(before.map((n) => [n.id, n]));
  const afterById = new Map(after.map((n) => [n.id, n]));
  const changed: { id: string; fromTenths: number; toTenths: number }[] = [];
  const added: string[] = [];

  for (const node of orderTree(after)) {
    const prior = beforeById.get(node.id);
    if (prior === undefined) {
      added.push(node.id);
      continue;
    }
    // The PARENT counts as a change too: effective weight is a product down the tree, so a
    // reparent moves the score without the node's own weight moving at all.
    if (prior.weightTenths !== node.weightTenths || prior.parentId !== node.parentId) {
      changed.push({ id: node.id, fromTenths: prior.weightTenths, toTenths: node.weightTenths });
    }
  }
  const removed = orderTree(before)
    .filter((n) => !afterById.has(n.id))
    .map((n) => n.id);

  return {
    changed,
    added,
    removed,
    anyChange: changed.length > 0 || added.length > 0 || removed.length > 0,
  };
}

/**
 * §6.1's gate, as one call: *"A model that does not balance can be saved as draft but cannot
 * be baselined, and steps 4-9 stay locked."*
 *
 * `empty` is reported separately from `balanced` for the reason `describeBalance` gives: an
 * empty model and an unbalanced one are two refusals with two messages
 * (`capability_model_is_empty` and `weights_do_not_balance`, API_CONTRACT §6.12).
 */
export interface BaselineGate {
  readonly canBaseline: boolean;
  readonly empty: boolean;
  readonly balance: WeightBalance;
  /** The 409 envelope's own sentence, or `null` when the model may be baselined. */
  readonly message: string | null;
}

export function baselineGate(nodes: readonly WeightNode[]): BaselineGate {
  const balance = describeBalance(nodes);
  const empty = nodes.length === 0;
  return {
    canBaseline: !empty && balance.balanced,
    empty,
    balance,
    message: empty
      ? 'The capability model is empty.'
      : balance.balanced
        ? null
        : summariseBalance(balance.groups),
  };
}
